import type { CSSProperties } from "react";
import type { Block } from "./types";
import { pickColor } from "./lib/palette";
import { toUnitBbox } from "./lib/bbox";

export interface OverlayBoxProps {
  block: Block;
  focused?: boolean;
  /** Visual mode. "edit" (drag handles) lands in PR #8 with page-review-ui. */
  mode?: "readonly" | "click";
  onClick?(block: Block): void;
  onHover?(block: Block | null): void;
}

/**
 * Single bbox button rendered as an absolutely-positioned element inside
 * a `position: relative` parent. % positioning means the parent must be
 * sized to the rendered page (via aspect-ratio, NOT a fixed pixel height —
 * see regression test in tests/PdfSurface.dom.test.tsx).
 */
export function OverlayBox(props: OverlayBoxProps) {
  const { block, focused, mode = "click", onClick, onHover } = props;
  const color = block.color ?? pickColor(block.label);
  const isInteractive = mode !== "readonly";
  // Tolerate raw VLM grounding boxes: auto-normalize a 0-1000 grid → 0-1.
  const bbox = toUnitBbox(block.bbox);

  const verdictBorderStyle: CSSProperties = block.verdict
    ? {
        borderStyle: block.verdict === "partial" ? "dashed" : "solid",
        borderColor:
          // Neutral var names with the legacy --okra-* kept as a fallback alias
          // (back-compat) and a hex default.
          block.verdict === "pass" ? "var(--citation-pass, var(--okra-pass, #2a6f3a))" :
          block.verdict === "partial" ? "var(--citation-warn, var(--okra-warn, #b7791f))" :
          "var(--citation-fail, var(--okra-fail, #c83232))",
        backgroundColor:
          block.verdict === "pass" ? "rgba(42,111,58,0.08)" :
          block.verdict === "partial" ? "rgba(183,121,31,0.08)" :
          "rgba(200,50,50,0.10)",
      }
    : {};

  // Codex P1 (round 3): non-focused overlays without a verdict were rendering
  // with a transparent border AND background — making the bboxes invisible.
  // Show every overlay with the palette color at low opacity by default so
  // the regions are always discoverable.
  const baseBorder = focused ? color : `${color}66`; // ~40% alpha
  const baseBackground = focused ? `${color}1f` : "transparent";

  const style: CSSProperties = {
    position: "absolute",
    left: `${bbox.x * 100}%`,
    top: `${bbox.y * 100}%`,
    width: `${bbox.w * 100}%`,
    height: `${bbox.h * 100}%`,
    border: `1.5px solid ${baseBorder}`,
    borderRadius: 2,
    cursor: isInteractive ? "pointer" : "default",
    boxSizing: "border-box",
    background: baseBackground,
    // Codex P2 (round 3): pointerEvents:none alone left readonly overlays
    // keyboard-activatable. For readonly we render a non-interactive <div>
    // (see element switch below); pointerEvents stays a hint, not a guarantee.
    pointerEvents: isInteractive ? "auto" : "none",
    ...verdictBorderStyle,
    // Lets a host stylesheet draw a label chip in the color of the box.
    ["--overlay-color" as string]: color,
  };

  // Readonly overlays render as a <div> — not in tab order, not activatable.
  if (!isInteractive) {
    return (
      <div
        role="presentation"
        aria-label={`overlay ${block.label} on page ${block.page}`}
        data-overlay-page={block.page}
        data-overlay-idx={block.idx}
        data-overlay-label={block.label}
        data-overlay-focused={focused ? "true" : undefined}
        style={style}
      />
    );
  }

  return (
    <button
      type="button"
      role="button"
      aria-label={`overlay ${block.label} on page ${block.page}`}
      data-overlay-page={block.page}
      data-overlay-idx={block.idx}
      data-overlay-label={block.label}
      data-overlay-focused={focused ? "true" : undefined}
      style={style}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(block);
      }}
      onMouseEnter={() => onHover?.(block)}
      onMouseLeave={() => onHover?.(null)}
    />
  );
}
